import * as ErrorReporting from './error-reporting'

/**
 * Patches console.error and console.warn to also forward to error reporting
 * (PostHog).
 *
 * - console.error → captureException (if Error) or captureMessage('error')
 * - console.warn  → captureMessage('warning'), i.e. PostHog Logs
 *
 * Includes rate-limiting and noise filtering to avoid flooding PostHog.
 */

const NOISE_PATTERNS = [
  // React internals
  /^Warning:/,
  /^React does not recognize/,
  /^Each child in a list should have a unique/,
  /^Cannot update a component/,
  /^Can't perform a React state update on an unmounted component/,
  // Redux DevTools
  /redux-persist/i,
  // Browser extension noise
  /^%c/,
  /chrome-extension/,
  /moz-extension/,
  // Development-only
  /Download the React DevTools/,
  // Error tracking service's own errors
  /Failed to track error/,
  // Facebook Pixel dev environment guard
  /Cannot use production pixel ID in development/,
  // Facebook Pixel blocked by ad blocker — expected
  /Facebook Pixel blocked/,
  // Duplicate Meta Pixel initialization — not user-facing
  /Duplicate Pixel ID/,
  // Google Sign-In SDK called multiple times — not actionable
  /\[GSI_LOGGER\]/,
  // Mixpanel SDK internal errors (mutex locking, network failures) — not actionable
  /Mixpanel error:/,
  // Same-site navigation warning — not actionable
  /You navigate from the same website/,
  // Meta Pixel invalid pixel ID — caught by ignoreErrors, double-filter here
  /\[Meta Pixel\] - Invalid PixelID/,
  // posthog-js logs its own rate-limit / transport complaints via console.error
  // unconditionally; forwarding them would fan out into PostHog again (bounded
  // by the rate limit below, but self-inflicted noise)
  /^\[PostHog\.js\]/,
]

const MAX_IDENTICAL_PER_MINUTE = 3
const recentMessages = new Map<string, { count: number; resetAt: number }>()

function isNoise(args: unknown[]): boolean {
  const first = args[0]
  if (typeof first !== 'string') return false
  return NOISE_PATTERNS.some(pattern => pattern.test(first))
}

function isRateLimited(key: string): boolean {
  const now = Date.now()
  const entry = recentMessages.get(key)

  if (!entry || now > entry.resetAt) {
    recentMessages.set(key, { count: 1, resetAt: now + 60_000 })
    return false
  }

  entry.count++
  return entry.count > MAX_IDENTICAL_PER_MINUTE
}

function getMessageKey(args: unknown[]): string {
  const first = args[0]
  if (first instanceof Error) return first.message
  if (typeof first === 'string') return first.slice(0, 200)
  return String(first).slice(0, 200)
}

function extractError(args: unknown[]): Error | null {
  for (const arg of args) {
    if (arg instanceof Error) return arg
  }
  return null
}

function argsToMessage(args: unknown[]): string {
  return args
    .map(arg => {
      if (arg instanceof Error) return arg.message
      if (typeof arg === 'string') return arg
      try {
        return JSON.stringify(arg)
      } catch {
        return String(arg)
      }
    })
    .join(' ')
}

export function patchConsoleToErrorReporting(): void {
  const originalError = console.error
  const originalWarn = console.warn

  console.error = (...args: unknown[]) => {
    // Always call original
    originalError.apply(console, args)

    if (isNoise(args) || isRateLimited(getMessageKey(args))) return

    const error = extractError(args)
    // `via: console` tells the PostHog router this capture came from the
    // bridge (it labels the issue/log `source: console`); the tag itself is
    // consumed there, never forwarded.
    if (error) {
      // Deferred a microtask: the house pattern is `console.error(msg, err)`
      // followed by `errorReportingService.captureException(err, { feature,
      // step })`. The explicit, tagged capture runs first and the bridge's bare
      // one is then deduped, instead of the other way round.
      queueMicrotask(() =>
        ErrorReporting.captureException(error, { tags: { via: 'console' } }, 'console.error')
      )
    } else {
      ErrorReporting.withScope(scope => {
        scope.setTag('via', 'console')
        ErrorReporting.captureMessage(argsToMessage(args), 'error')
      })
    }
  }

  console.warn = (...args: unknown[]) => {
    // Always call original
    originalWarn.apply(console, args)

    if (isNoise(args) || isRateLimited(getMessageKey(args))) return

    ErrorReporting.withScope(scope => {
      scope.setTag('via', 'console')
      ErrorReporting.captureMessage(argsToMessage(args), 'warning')
    })
  }
}
